import * as Yup from 'yup';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	Skeleton,
} from '@mui/material';
import { Field, Formik, FormikHelpers } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

import { faPlus, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useApi, useAuth } from '../shared/context';
import { getApiResponseData, getResponseData } from '../shared/utils/axios';
import {
	CollateralSecurityTypeMap,
	CollateralSecurityTypes,
	DealDebtSchedule,
	FacilityTypes,
	LenderTypeMap,
	LenderTypes,
} from '../shared/models/DealDebtSchedule';

import {
	CerebroBreadcrumb,
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroIcon,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroTextButton,
	CerebroYesNo,
	DebtCapitalHeading,
} from '../shared/components';

import { CerebroApiEndpoints } from '../shared/utils/api';
import {
	QueryKeys,
	useDataRoomTermSheetsQuery,
	useDealQuery,
	useDebtScheduleQuery,
	useNavigateToNextPage,
} from '../shared/hooks';
import { DateUtils, FormikUtils, HtmlUtils, NumberUtils } from '../shared/utils';

import { ReactComponent as ReceivablesFinancing } from '../shared/icons/ReceivablesFinancing.svg';
import { AddTermSheetForm } from './ExistingDebt/AddTermSheetForm';
import { ExistingDebtTermListItem } from './ExistingDebt/ExistingDebtTermListItem';
import { TermSheet } from '../shared/models';
import { DeleteDebtTermListItemDialog } from './ExistingDebt/DeleteDebtTermListItemDialog';
import { DebtScheduleListItem } from './ExistingDebt/DebtScheduleListItem';
import { MaxCurrencyLength } from '../shared/utils/platform';
import { validateDateWithinPlusMinus100Years } from '../shared/utils/date';

export const DebtSchedule: React.FC = () => {
	const api = useApi();
	const navigate = useNavigate();
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();
	const dealQuery = useDealQuery(guid);
	const existingTermsQuery = useDataRoomTermSheetsQuery(guid, {
		enabled: currentUser.isEmployeeUser,
		retry: false,
	});

	const navigateToNextPage = useNavigateToNextPage();

	const [addingOrEditingTermLoan, setAddingOrEditingTermLoan] = React.useState(false);
	const [addingOrEditingRevolver, setAddingOrEditingRevolver] = React.useState(false);
	const [addExistingDebtTerm, setAddExistingDebtTerm] = React.useState(false);

	const [scheduleBeingEdited, setScheduleBeingEdited] = React.useState<DealDebtSchedule | null>(null);
	const [deleteExistingDebtTerm, setDeleteExistingDebtTerm] = React.useState<TermSheet | null>(null);

	const snackbar = useSnackbar();
	const client = useQueryClient();

	const debtScheduleQuery = useDebtScheduleQuery(guid);

	const onCloseDrawersAndDialogs = () => {
		setAddingOrEditingRevolver(false);
		setAddingOrEditingTermLoan(false);
		setAddExistingDebtTerm(false);
		setScheduleBeingEdited(null);
		setDeleteExistingDebtTerm(null);
	};

	const setEditExistingDebtTerm = (term: TermSheet) => {
		const revision = term.revisions[0];
		navigate(`/${guid}/DebtSchedule/${term.guid}/Revisions/${revision?.guid}`);
	};

	const { mutate: addOrUpdate } = useMutation(
		async ({ schedulePatch }: AddUpdateMutationVariables) => {
			if (schedulePatch.guid === undefined) {
				const { data, error } = await getApiResponseData<DealDebtSchedule>(
					api.post(CerebroApiEndpoints.DebtSchedules.Root(guid), schedulePatch)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			const { data, error } = await getApiResponseData<DealDebtSchedule>(
				api.patch(CerebroApiEndpoints.DebtSchedules.Specific(guid, schedulePatch.guid), schedulePatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.invalidateQueries(QueryKeys.DebtSchedule(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Changes Saved.', { variant: 'success' });

				if (data?.isTermLoan) setAddingOrEditingTermLoan(false);
				else setAddingOrEditingRevolver(false);

				setScheduleBeingEdited(null);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { setSubmitting }) => {
				setSubmitting(false);
			},
		}
	);

	const onAddTermLoan = () => {
		setAddingOrEditingTermLoan(true);
		setScheduleBeingEdited(null);
	};

	const onAddRevolver = () => {
		setAddingOrEditingRevolver(true);
		setScheduleBeingEdited(null);
	};

	const onAddDebtTerm = () => {
		setAddExistingDebtTerm(true);
		setScheduleBeingEdited(null);
	};

	const onEdit = (schedule: DealDebtSchedule) => {
		if (schedule.isTermLoan) {
			setAddingOrEditingTermLoan(true);
			setScheduleBeingEdited(schedule);
		} else {
			setAddingOrEditingRevolver(true);
			setScheduleBeingEdited(schedule);
		}
	};

	const buildSubmitHandler =
		(facilityType: FacilityTypes) =>
		(values: DebtScheduleFormValues, { setSubmitting }: FormikHelpers<DebtScheduleFormValues>) => {
			const schedulePatch: Partial<DealDebtSchedule> = {
				lenderType: values.lenderType,
				presentBalance: NumberUtils.ParseDecimal(values.presentBalance),
				monthlyPayment: NumberUtils.ParseDecimal(values.monthlyPayment),
				originalLoanAmount: NumberUtils.ParseDecimal(values.originalLoanAmount),
				interestRate: NumberUtils.ParseDecimal(values.interestRate, undefined),
				amortization: NumberUtils.ParseDecimal(values.amortization),
				maturityDate: values.maturityDate?.toISOString(),
				collateralSecurityType: values.collateralSecurityType,
				isRefinance: values.isRefinance,
				facilityType: facilityType,
				notes: values.notes,
			};

			if (values.guid !== '') schedulePatch.guid = values.guid;

			addOrUpdate({ setSubmitting, schedulePatch });
		};

	const [scheduleBeingDeleted, setScheduleBeingDeleted] = React.useState<DealDebtSchedule | null>(null);

	const { mutate: deleteSchedule } = useMutation<DealDebtSchedule, unknown, DealDebtSchedule>(
		scheduleToDelete =>
			getResponseData(
				api.delete<DealDebtSchedule>(CerebroApiEndpoints.DebtSchedules.Specific(guid, scheduleToDelete.guid))
			),
		{
			onMutate: () => {
				setScheduleBeingDeleted(null);
			},
			onSuccess: _data => {
				client.invalidateQueries(QueryKeys.DebtSchedule(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Debt Schedule Item Deleted.', { variant: 'success' });

				setScheduleBeingDeleted(null);
				setScheduleBeingEdited(null);
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.DebtSchedule(guid));
				snackbar.enqueueSnackbar('There was a problem deleting the debt schedule item.', { variant: 'error' });
			},
		}
	);

	const onDelete = (schedule: DealDebtSchedule) => {
		setScheduleBeingDeleted(schedule);
	};

	const onDeleteConfirmation = () => {
		if (scheduleBeingDeleted !== null) deleteSchedule(scheduleBeingDeleted);
	};

	const onDeleteCancellation = () => {
		setScheduleBeingDeleted(null);
	};

	const onSaveAndContinue = () => {
		snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
		navigateToNextPage();
	};

	const amountRequested = NumberUtils.Sum(
		...(debtScheduleQuery.data?.filter(item => item.isRefinance === true).map(item => item.presentBalance ?? 0) ??
			[])
	);

	return (
		<>
			<CerebroPageContainer>
				<CerebroBreadcrumb>Debt</CerebroBreadcrumb>

				<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
					Provide information about your company’s existing debt.
				</CerebroHeading>

				<CerebroText sx={{ marginBottom: 6 }}>
					Information about your existing debt is critical to helping you get an accurate assessment of your
					financing request.
				</CerebroText>

				<DebtCapitalHeading icon={ReceivablesFinancing} amountRequested={amountRequested}>
					Debt Schedule Items
				</DebtCapitalHeading>

				{debtScheduleQuery.isLoading && <Skeleton height={200} />}

				{debtScheduleQuery.isSuccess && debtScheduleQuery.data.length > 0 && (
					<>
						<Grid display={'flex'}>
							<Grid item xs={6} marginRight={2}>
								<Card
									sx={theme => ({
										border: `2px dashed ${theme.palette.primary.main}`,
										height: '100%',
										marginBottom: 2,
										maxHeight: 175,
										width: '100%',
									})}
								>
									<CardActionArea sx={{ height: '100%' }} onClick={() => onAddTermLoan()}>
										<CardContent>
											<CerebroText align="center" sx={{ marginBottom: 2 }}>
												Add Term Loan
											</CerebroText>
											<CerebroText align="center" color="primary">
												<FontAwesomeIcon size="2x" icon={faPlus} />
											</CerebroText>
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>

							<Grid item xs={6}>
								<Card
									sx={theme => ({
										border: `2px dashed ${theme.palette.primary.main}`,
										height: '100%',
										marginBottom: 2,
										maxHeight: 175,
										width: '100%',
									})}
								>
									<CardActionArea sx={{ height: '100%' }} onClick={() => onAddRevolver()}>
										<CardContent>
											<CerebroText align="center" sx={{ marginBottom: 2 }}>
												Add Revolver / Line of Credit
											</CerebroText>
											<CerebroText align="center" color="primary">
												<FontAwesomeIcon size="2x" icon={faPlus} />
											</CerebroText>
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>
						</Grid>

						{debtScheduleQuery.data.map(schedule => (
							<DebtScheduleListItem
								key={schedule.guid}
								schedule={schedule}
								onEdit={onEdit}
								onDelete={onDelete}
							/>
						))}
					</>
				)}

				{debtScheduleQuery.isSuccess && debtScheduleQuery.data.length === 0 && (
					<Grid item xs={12}>
						<Card
							sx={theme => ({
								border: `2px dashed ${theme.palette.primary.main}`,
								height: '100%',
								marginBottom: 2,
								maxHeight: 175,
								width: '100%',
							})}
						>
							<CardActionArea sx={{ height: '100%' }} onClick={() => onAddTermLoan()}>
								<CardContent>
									<CerebroText align="center" sx={{ marginBottom: 2 }}>
										Add Term Loan
									</CerebroText>
									<CerebroText align="center" color="primary">
										<FontAwesomeIcon size="2x" icon={faPlus} />
									</CerebroText>
								</CardContent>
							</CardActionArea>
						</Card>

						<Card
							sx={theme => ({
								border: `2px dashed ${theme.palette.primary.main}`,
								height: '100%',
								marginBottom: 2,
								maxHeight: 175,
								width: '100%',
							})}
						>
							<CardActionArea sx={{ height: '100%' }} onClick={() => onAddRevolver()}>
								<CardContent>
									<CerebroText align="center" sx={{ marginBottom: 2 }}>
										Add Revolver / Line of Credit
									</CerebroText>
									<CerebroText align="center" color="primary">
										<FontAwesomeIcon size="2x" icon={faPlus} />
									</CerebroText>
								</CardContent>
							</CardActionArea>
						</Card>
					</Grid>
				)}

				{currentUser.isEmployeeUser && dealQuery.isSuccess && existingTermsQuery.isSuccess && (
					<>
						<Box marginTop={6}>
							<DebtCapitalHeading icon={ReceivablesFinancing} amountRequested={0}>
								Existing Debt Terms{' '}
								<CerebroText component="span" variant="hint">
									(Employee Only)
								</CerebroText>
							</DebtCapitalHeading>

							{currentUser.isEmployeeUser && (
								<CerebroText sx={{ marginTop: 2, marginBottom: 4 }}>
									Use this section to enter as much detail as possible for the customer&#39;s existing
									debt. This data can be used for analysis and for targeting refinance opportunities.
								</CerebroText>
							)}
						</Box>

						<Grid display={'flex'}>
							<Grid item xs={12} marginRight={2}>
								<Card
									sx={theme => ({
										border: `2px dashed ${theme.palette.primary.main}`,
										height: '100%',
										marginBottom: 2,
										maxHeight: 175,
										width: '100%',
									})}
								>
									<CardActionArea sx={{ height: '100%' }} onClick={() => onAddDebtTerm()}>
										<CardContent>
											<CerebroText align="center" sx={{ marginBottom: 2 }}>
												Add Existing Debt Term
											</CerebroText>
											<CerebroText align="center" color="primary">
												<FontAwesomeIcon size="2x" icon={faPlus} />
											</CerebroText>
										</CardContent>
									</CardActionArea>
								</Card>
							</Grid>
						</Grid>

						{existingTermsQuery.data.map(term => (
							<ExistingDebtTermListItem
								key={term.guid}
								debtTerm={term}
								onEdit={setEditExistingDebtTerm}
								onDelete={setDeleteExistingDebtTerm}
							/>
						))}

						<AddTermSheetForm
							dealGuid={guid}
							deal={dealQuery.data}
							isAdding={addExistingDebtTerm}
							close={onCloseDrawersAndDialogs}
						/>
						<DeleteDebtTermListItemDialog
							dealGuid={guid}
							debtTerm={deleteExistingDebtTerm}
							close={onCloseDrawersAndDialogs}
						/>
					</>
				)}

				<CerebroPrimaryButton sx={{ marginTop: 3 }} onClick={onSaveAndContinue}>
					Save &amp; Continue
				</CerebroPrimaryButton>
			</CerebroPageContainer>

			<Dialog open={scheduleBeingDeleted !== null} onClose={onDeleteCancellation}>
				<DialogTitle>{'Confirm Delete'}</DialogTitle>
				<DialogContent>
					<DialogContentText>Are you sure you want to delete this debt schedule item?</DialogContentText>
				</DialogContent>
				<DialogActions>
					<CerebroTextButton onClick={onDeleteCancellation}>No</CerebroTextButton>
					<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
				</DialogActions>
			</Dialog>

			{addingOrEditingTermLoan && (
				<DebtScheduleDrawer
					isTermLoan
					schedule={scheduleBeingEdited}
					onSubmit={buildSubmitHandler('Term_Loan')}
					onClose={() => setAddingOrEditingTermLoan(false)}
				/>
			)}

			{addingOrEditingRevolver && (
				<DebtScheduleDrawer
					schedule={scheduleBeingEdited}
					onSubmit={buildSubmitHandler('Revolver')}
					onClose={() => setAddingOrEditingRevolver(false)}
				/>
			)}
		</>
	);
};

interface DebtScheduleDrawerProps {
	isTermLoan?: boolean;

	schedule: DealDebtSchedule | null;

	onClose: () => void;
	onSubmit: (values: DebtScheduleFormValues, helpers: FormikHelpers<DebtScheduleFormValues>) => void;
}

const DebtScheduleDrawer: React.FC<DebtScheduleDrawerProps> = ({ isTermLoan = false, schedule, onSubmit, onClose }) => {
	const { currentUser } = useAuth();
	const ref = React.useRef<HTMLDivElement>(null);

	const [additionalFieldsVisible, setAdditionalFieldsVisible] = React.useState<boolean>(
		Boolean(schedule?.amortization) &&
			Boolean(schedule?.monthlyPayment) &&
			Boolean(schedule?.maturityDate) &&
			Boolean(schedule?.lenderType)
	);

	return (
		<CerebroDrawer ref={ref} open onClose={onClose} icon={ReceivablesFinancing}>
			<CerebroHeading sx={{ marginBottom: 3 }}>
				{isTermLoan ? 'Term Loan' : 'Revolver/Line of Credit'}
			</CerebroHeading>
			<Formik
				validationSchema={DebtScheduleItemSchema}
				onSubmit={onSubmit}
				initialValues={{
					guid: schedule?.guid ?? '',
					lenderType: schedule?.lenderType ?? 'Unknown',
					presentBalance: NumberUtils.FormatCurrency(schedule?.presentBalance),
					monthlyPayment: NumberUtils.FormatCurrency(schedule?.monthlyPayment),
					originalLoanAmount: NumberUtils.FormatCurrency(schedule?.originalLoanAmount),
					interestRate: NumberUtils.FormatFloat(schedule?.interestRate, 3),
					amortization: NumberUtils.FormatNumber(schedule?.amortization),
					maturityDate: DateUtils.ParseDateOnly(schedule?.maturityDate),
					collateralSecurityType: schedule?.collateralSecurityType ?? null,
					isRefinance: schedule?.isRefinance ?? null,
					notes: schedule?.notes ?? '',
				}}
			>
				{({ isSubmitting, touched, errors, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						onSubmit={handleSubmit}
						sx={{ paddingRight: 3, width: { xs: '100%', md: 600 } }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<Field name="guid" type="hidden" />

						<Grid container spacing={2} alignItems="flex-end">
							{!isTermLoan && (
								<Grid item xs={6}>
									<CerebroLabel htmlFor="originalLoanAmount" sx={{ marginTop: 2 }}>
										Commitment Amount
									</CerebroLabel>
									<Field
										formatas="currency"
										id="originalLoanAmount"
										name="originalLoanAmount"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										placeholder="$0"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							<Grid item xs={6}>
								<CerebroLabel htmlFor="presentBalance" sx={{ marginTop: 2 }}>
									Present Balance
								</CerebroLabel>
								<Field
									formatas="currency"
									id="presentBalance"
									name="presentBalance"
									as={CerebroMaskedInput}
									placeholder="$0"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel htmlFor="collateralSecurityType" sx={{ marginTop: 2 }}>
									Collateral / Security
								</CerebroLabel>
								<Field
									as={CerebroDropdown}
									id="collateralSecurityType"
									name="collateralSecurityType"
									disabled={isSubmitting}
									error={
										errors.collateralSecurityType !== undefined && touched.collateralSecurityType
									}
								>
									<CerebroDropdownMenuItem value="none">--</CerebroDropdownMenuItem>

									{Object.entries(CollateralSecurityTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel htmlFor="interestRate" sx={{ marginTop: 2 }}>
									Interest Rate
								</CerebroLabel>
								<Field
									allowDecimals
									minDecimalPlaces={3}
									maxDecimalPlaces={3}
									formatas="percent"
									id="interestRate"
									name="interestRate"
									as={CerebroMaskedInput}
									placeholder="0.000%"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="maturityDate" required>
									Maturity Date
								</CerebroLabel>
								<Field
									id="maturityDate"
									name="maturityDate"
									component={CerebroDatePicker}
									disabled={isSubmitting}
									error={touched.maturityDate && errors.maturityDate !== undefined}
								/>
								{touched.maturityDate && errors.maturityDate && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.maturityDate}
									</CerebroText>
								)}
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="isRefinance" sx={{ marginTop: 2 }}>
									Would you like to refinance this facility?
								</CerebroLabel>
								<Field
									id="isRefinance"
									name="isRefinance"
									small
									as={CerebroYesNo}
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('isRefinance', errors, touched)}
								/>
								{FormikUtils.IsInvalid('isRefinance', errors, touched) && (
									<CerebroText variant="error">{errors.isRefinance}</CerebroText>
								)}
							</Grid>
						</Grid>

						{currentUser && currentUser.isEmployeeUser && (
							<>
								<CerebroLabel htmlFor="notes" sx={{ marginTop: 3 }}>
									Notes{' '}
									<CerebroText variant="hint" component="span">
										(Visible to Cerebro Only)
									</CerebroText>
								</CerebroLabel>
								<Field id="notes" name="notes" as={CerebroTextarea} disabled={isSubmitting} rows={5} />
							</>
						)}

						<Button
							fullWidth
							size="small"
							onClick={() => setAdditionalFieldsVisible(current => !current)}
							sx={theme => ({
								color: theme.palette.text.primary,
								display: 'flex',
								justifyContent: 'space-between',
								marginTop: 3,
								paddingX: 2,
								textTransform: 'unset',
							})}
						>
							<CerebroText component="span">
								{additionalFieldsVisible ? 'Hide' : 'Add'} Additional Details
							</CerebroText>
							<CerebroIcon>
								<FontAwesomeIcon icon={additionalFieldsVisible ? faChevronUp : faChevronDown} />
							</CerebroIcon>
						</Button>
						<Divider />

						<Collapse in={additionalFieldsVisible}>
							<Grid container spacing={2} alignItems="flex-end" sx={{ paddingX: 2 }}>
								<Grid item xs={6}>
									<CerebroLabel htmlFor="lenderType" sx={{ marginTop: 2 }}>
										Lender Type
									</CerebroLabel>
									<Field
										as={CerebroDropdown}
										id="lenderType"
										name="lenderType"
										disabled={isSubmitting}
										error={errors.lenderType !== undefined && touched.lenderType}
									>
										{Object.entries(LenderTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>

								{isTermLoan && (
									<>
										<Grid item xs={6}>
											<CerebroLabel htmlFor="originalLoanAmount" sx={{ marginTop: 2 }}>
												Original Loan Amount
											</CerebroLabel>
											<Field
												formatas="currency"
												id="originalLoanAmount"
												name="originalLoanAmount"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												placeholder="$0"
												disabled={isSubmitting}
											/>
										</Grid>

										<Grid item xs={6}>
											<CerebroLabel htmlFor="monthlyPayment" sx={{ marginTop: 2 }}>
												Monthly Payment
											</CerebroLabel>
											<Field
												formatas="currency"
												id="monthlyPayment"
												name="monthlyPayment"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												placeholder="$0"
												disabled={isSubmitting}
											/>
										</Grid>

										<Grid item xs={6}>
											<CerebroLabel htmlFor="amortization" sx={{ marginTop: 2 }}>
												Amortization{' '}
												<CerebroText variant="hint" component="span">
													(in years)
												</CerebroText>
											</CerebroLabel>
											<Field
												id="amortization"
												name="amortization"
												as={CerebroInput}
												placeholder="0"
												disabled={isSubmitting}
											/>
										</Grid>
									</>
								)}
							</Grid>
						</Collapse>

						<CerebroPrimaryButton loading={isSubmitting} wide type="submit" sx={{ marginTop: 3 }}>
							Save Changes
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface DebtScheduleFormValues {
	monthlyPayment: string;
	amortization: string | null;
	guid: string;
	lenderType: LenderTypes;
	presentBalance: string;
	originalLoanAmount: string;
	interestRate: string;
	maturityDate: Date | null;
	collateralSecurityType: CollateralSecurityTypes | null;
	isRefinance: boolean | null;
	notes: string;
}

const DebtScheduleItemSchema = Yup.object().shape({
	isRefinance: Yup.boolean()
		.nullable(true)
		.required('Please indicate whether you wish to refinance this facility'),

	maturityDate: Yup.date()
		.nullable()
		.test(
			'maturityDate-range',
			'Maturity Date must be within 100 years of today.',
			(value) => {
				if (!value) return true;
				return !validateDateWithinPlusMinus100Years(value, 'Maturity Date');
			}
		)
		.typeError('Maturity Date must be a valid date'),
});

type AddUpdateMutationVariables = {
	setSubmitting: (isSubmitting: boolean) => void;
	schedulePatch: Partial<DealDebtSchedule>;
};
